import { Controller } from '@hotwired/stimulus';

// Map marker colors (matching Bootstrap 5 utility colors)
const MAP_COLORS = {
  PRIMARY: '0x24AA2E'   // Bootstrap green-600 equivalent
};

// Utility functions for coordinate validation and operations
const CoordinateUtils = {
  isValid(latitude, longitude) {
    return latitude != null && longitude != null &&
           !Number.isNaN(latitude) && !Number.isNaN(longitude) &&
           typeof latitude === 'number' && typeof longitude === 'number';
  },

  sanitize(latitude, longitude) {
    return this.isValid(latitude, longitude) ? { latitude, longitude } : null;
  },

  createMarker(latitude, longitude, radiusMiles) {
    const coords = this.sanitize(latitude, longitude);
    return coords && new Marker(coords.latitude, coords.longitude, { radiusMiles });
  }
};

const MapUtils = {
  showDefaultMap(mainCoords, markers) {
    const hasValidMainCoords = CoordinateUtils.isValid(mainCoords.latitude, mainCoords.longitude);
    const allMarkersCleared = Object.values(markers || {}).every(item => item.cleared === true);
    return !hasValidMainCoords && allMarkersCleared;
  },

  filterValidMarkers(markers) {
    return Object.values(markers || {})
      .filter(marker => marker && marker.latitude && marker.longitude && !marker.cleared)
      .filter(marker => CoordinateUtils.isValid(marker.latitude, marker.longitude));
  },

  createMarkerString(markers) {
    const validMarkers = markers.filter(marker => marker && marker.toString());
    if (validMarkers.length === 0) return '';

    const [mainMarker, ...additionalMarkers] = validMarkers;
    const mainMarkerString = `size:mid|color:${MAP_COLORS.PRIMARY}|${mainMarker.toString()}`;
    const additionalMarkerStrings = additionalMarkers.map(marker => marker.toString());

    return [mainMarkerString, ...additionalMarkerStrings].join('|');
  }
};

class Marker {
  constructor(latitude, longitude, { radiusMiles }) {
    this.latitude = latitude;
    this.longitude = longitude;
    this.radiusMiles = radiusMiles;
  }

  get coordinates() {
    return [this.latitude, this.longitude];
  }

  toString() {
    return `${this.latitude},${this.longitude}`;
  }
}

export default class extends Controller {
  static targets = ['image'];
  static values = {
    latitude: Number,
    longitude: Number,
    apiKey: String,
    markers: Object,
    defaultLocation: String
  };

  connect() {
    this.setMapUrl();
    document.addEventListener('zipcode:loaded', this.handleZipcodeLoaded.bind(this));
    document.addEventListener('zipcode:cleared', this.handleZipcodeCleared.bind(this));
  }

  disconnect() {
    document.removeEventListener('zipcode:loaded', this.handleZipcodeLoaded.bind(this));
    document.removeEventListener('zipcode:cleared', this.handleZipcodeCleared.bind(this));
  }

  handleZipcodeLoaded({ detail }) {
    const { latitude, longitude, clazz, persisted } = detail;

    if (clazz === 'location') {
      this.handleLocationZipcodeFound({ detail });
    } else if (clazz === 'posting_area' && persisted) {
      this.handleEditPostingAreaZipcodeFound({ detail });
    } else if (clazz === 'posting_area') {
      this.handleNewPostingAreaZipcodeFound({ detail });
    } else {
      console.warn('Unknown type:', clazz);
      return;
    }
  }

  handleEditPostingAreaZipcodeFound({ detail: { latitude, longitude, clazz_id, id, zip, persisted } }) {
    // Create a completely new object
    const updatedMarkers = {};
    Object.keys(this.markersValue).forEach(key => {
      if (key == clazz_id) {
        updatedMarkers[key] = {
          latitude: latitude,
          longitude: longitude,
          id: id,
          zip: zip,
          persisted: persisted,
          cleared: false // Ensure cleared flag is reset when zipcode is found
        };
      } else {
        updatedMarkers[key] = this.markersValue[key];
      }
    });

    // Apply the new markers object to force a complete refresh
    this.markersValue = updatedMarkers;

    // Regenerate the map with new coordinates
    this.setMapUrl();
  }

  handleNewPostingAreaZipcodeFound({ detail: { latitude, longitude, clazz_id, id, zip, persisted } }) {
    // Create a copy of existing markers and add the new marker
    // Use the provided UUID (from clazz_id) as the marker key
    const updatedMarkers = {
      ...this.markersValue,
      [clazz_id]: {
        latitude: latitude,
        longitude: longitude,
        id: id,
        zip: zip,
        persisted: persisted,
        cleared: false // Ensure cleared flag is set to false for new markers
      }
    };

    // Apply the new markers object to force a complete refresh
    this.markersValue = updatedMarkers;

    // Regenerate the map with new coordinates
    this.setMapUrl();
  }

  handleLocationZipcodeFound({ detail: { latitude, longitude, id, zip } }) {
    this.latitudeValue = latitude;
    this.longitudeValue = longitude;

    // Regenerate the map with new coordinates
    this.setMapUrl();
  }

  handleZipcodeCleared({ detail }) {
    const { clazz, clazz_id, persisted } = detail;

    if (clazz === 'location') {
      // Clear the location coordinates when zipcode is cleared
      this.latitudeValue = null;
      this.longitudeValue = null;
      this.setMapUrl();
    } else if (clazz === 'posting_area') {
      // Mark the marker as cleared but keep it in markersValue for future updates
      const updatedMarkers = { ...this.markersValue };
      if (updatedMarkers[clazz_id]) {
        updatedMarkers[clazz_id] = {
          ...updatedMarkers[clazz_id],
          cleared: true
        };
        this.markersValue = updatedMarkers;
      }
      this.setMapUrl();
    } else {
      console.warn('Unknown type for cleared zipcode:', clazz);
    }
  }

  setMapUrl() {
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const params = this.mapParams;
    const urlParams = new URLSearchParams(params);
    let queryString = urlParams.toString();

    this.imageTarget.src = `${baseUrl}?${queryString}`;
  }

  // https://developers.google.com/maps/documentation/maps-static/start#feature-parameters
  get mapParams() {
    if (this.showDefaultMap()) {
      return this.getDefaultMapParams();
    }

    const mainMarker = this.createMarker(this.latitudeValue, this.longitudeValue);
    const additionalMarkers = this.createAdditionalMarkers();
    const allMarkers = [mainMarker, ...additionalMarkers].filter(Boolean);

    return this.getMarkerMapParams(allMarkers);
  }

  showDefaultMap() {
    return MapUtils.showDefaultMap(
      { latitude: this.latitudeValue, longitude: this.longitudeValue },
      this.markersValue || {}
    );
  }

  getDefaultMapParams() {
    return {
      scale: 2,
      size: `${this.element.offsetWidth}x300`,
      maptype: 'roadmap',
      center: this.defaultLocationValue,
      zoom: 10,
      key: this.apiKeyValue,
    };
  }

  getMarkerMapParams(allMarkers) {
    if (allMarkers.length === 0) {
      return this.getDefaultMapParams();
    }

    return {
      scale: 2,
      size: `${this.element.offsetWidth}x300`,
      maptype: 'roadmap',
      markers: MapUtils.createMarkerString(allMarkers),
      key: this.apiKeyValue,
    };
  }

  createMarker(latitude, longitude) {
    return CoordinateUtils.createMarker(latitude, longitude, this.radiusMilesValue);
  }

  createAdditionalMarkers() {
    if (!this.hasMarkersValue || typeof this.markersValue !== 'object') {
      return [];
    }

    const validMarkers = MapUtils.filterValidMarkers(this.markersValue);
    return validMarkers.map(marker =>
      CoordinateUtils.createMarker(marker.latitude, marker.longitude, this.radiusMilesValue)
    ).filter(Boolean);
  }
}
